import { DELIVERY_PREFERENCE_OPTIONS_MAP } from '@/components/deal/delivery-preference/deal-delivery-preference-selector';
import { Checkbox } from '@/components/ui/checkbox';
import { ComboboxPopover } from '@/components/ui/combobox/combobox-popover';
import { cn, pluralize } from '@/lib/utils';
import type { Deal } from '@/models/registry';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useFilteredDealsContext } from '../filtered-deals/filtered-deals-provider';
import type { DeliveryPreferenceFilters } from '../types';

const DeliveryPreferenceFiltersComboBoxPopover = ({
  children,
  comboBoxOnChange,
  comboBoxOpen,
  onOpenChange,
  filters: deliveryPreferenceFilters,
  stayOpenAfterSelection = true,
  alignOffset,
  side = 'bottom',
  align,
  popoverContentClassName,
}: {
  children: ReactNode;
  comboBoxOnChange: (value: string) => void;
  comboBoxOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: DeliveryPreferenceFilters;
  stayOpenAfterSelection: boolean;
  alignOffset?: number;
  side?: 'left' | 'top' | 'right' | 'bottom' | undefined;
  align?: 'center' | 'start' | 'end' | undefined;
  popoverContentClassName?: string;
}) => {
  const { getCount } = useFilteredDealsContext();

  const comboBoxOptions = useMemo(() => {
    return DELIVERY_PREFERENCE_OPTIONS_MAP.map((option) => {
      const count = getCount(
        'delivery_preference',
        option.value as Deal['delivery_preference'],
      );

      return {
        label: (
          <div className="flex flex-row items-center gap-2 text-nowrap">
            {option.icon}
            {option.label}
            <span className="text-xs text-gray-400">
              {count} {pluralize(count, 'deal', 'deals')}
            </span>
          </div>
        ),
        value: option.value,
        icon: (
          <div className="size-4">
            <Checkbox
              checked={
                deliveryPreferenceFilters[
                  option.value as Deal['delivery_preference']
                ]
              }
            />
          </div>
        ),
      };
    });
  }, [deliveryPreferenceFilters, getCount]);

  return (
    <ComboboxPopover
      placeholder={'Delivery Preference...'}
      options={comboBoxOptions}
      onChange={comboBoxOnChange}
      align={align}
      side={side}
      alignOffset={alignOffset}
      // TODO: Add end adornment and empty message
      endAdornment={undefined}
      emptyMessage={undefined}
      open={comboBoxOpen}
      onOpenChange={onOpenChange}
      stayOpenAfterSelection={stayOpenAfterSelection}
      popoverContentClassName={cn(
        'w-fit min-w-[200px]',
        popoverContentClassName,
      )}
    >
      {children}
    </ComboboxPopover>
  );
};

export default DeliveryPreferenceFiltersComboBoxPopover;
